import {Expression, Operand, Operator} from '../types/expression-types';
import {Serializable} from '../types/serialized-cohort-types';
import ExpressionNode from './expression-node';
import OperandNode from './operand-node';
import {expressionToClauseRoot, expressionToLiteralRoot} from './utils';

export default class OperatorNode extends ExpressionNode implements Serializable {
  public static parseExpression(expression: Expression | Operand): ExpressionNode {
    if (expression) {
      if (OperatorNode.isExpression(expression)) {
        const [left, right] = expression.children;
        return new OperatorNode(
          expression.operator,
          OperatorNode.parseExpression(left),
          OperatorNode.parseExpression(right),
        );
      } else {
        const operand = expression;
        return new OperandNode(operand);
      }
    }

    return null;
  }

  private static isExpression(expression: Expression | Operand): expression is Expression {
    return (expression as Expression).operator !== undefined;
  }

  constructor(public operator: Operator, public left: ExpressionNode, public right: ExpressionNode) {
    super(left, right);
  }

  public serialize(): Expression {
    return {
      children: [this.left, this.right].filter(Boolean).map((child) => child.serialize()),
      operator: this.operator,
    };
  }

  /**
   * To note the boundary of literals and clauses, we need to embed some additional information in the tree.
   * For a literal A, we add a parent node that translates the expression to "A and true", which is logically
   * equivalent but allows us to distinguish between "A1 and A2" vs "A and B".
   * For clauses, we use a similar trick, adding a parent node to make the expression "A1 or false".
   * Multi-value operators such as between and string equals require this transformation.
   */
  public toLiteralRoot(): OperatorNode {
    return expressionToLiteralRoot(this);
  }

  public toClauseRoot(): OperatorNode {
    return expressionToClauseRoot(this);
  }
}
